import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewInit,
  ElementRef
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, Subject, fromEvent, takeUntil } from 'rxjs';
import { CommonService } from 'src/app/services/common/common.service';
import { AddressService } from 'src/app/services/register-data/addresses/address.service';
import { ValidatorService } from 'src/app/services/validators/errors-validators.service';
import { Address } from 'src/models/interfaces/address/address-interface';
import { CepResponseModel } from 'src/models/interfaces/address/cep-response-model';
import { TypeAccess } from 'src/models/interfaces/input-interface';

@Component({
  selector: 'spe-new-address',
  templateUrl: './new-address.component.html',
  styleUrls: ['./new-address.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewAddressComponent implements OnInit, AfterViewInit, OnDestroy {
  public listAddressType: TypeAccess[] = [];
  public newZipCode = '';
  public submitted = false;
  public cepResponse?: CepResponseModel;
  public message = '';
  public address!: Address;
  public hasScrolled = false;
  public showFields = false;

  private subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();

  @ViewChild('dialogScrollArea', { static: true })
  dialogScrollAreaRef!: ElementRef<HTMLDivElement>;

  public addressDataForm = new FormGroup({
    addressType: new FormControl('', [Validators.required]),
    zipCode: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(8)]),
    complement: new FormControl(''),
    reference: new FormControl(''),
    number: new FormControl('', [Validators.required]),
    responsible: new FormControl('', [Validators.required]),
    street: new FormControl('', [Validators.required]),
    district: new FormControl('', [Validators.required]),
    city: new FormControl({ value: '', disabled: true }, [Validators.required]),
    state: new FormControl({ value: '', disabled: true }, [Validators.required])
  });

  validatorErrors = this.validatorService.validatorErrors;

  constructor(
    private dialogRef: MatDialogRef<NewAddressComponent>,
    private addressService: AddressService,
    public readonly validatorService: ValidatorService,
    public readonly commonService: CommonService,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: { address?: Address; desabilitarApagar?: boolean }
  ) {}

  get isEditMode(): boolean {
    return !!this.data?.address;
  }

  get desabilitarApagar(): boolean {
    return !!this.data.desabilitarApagar;
  }

  get addressDataFormControl() {
    return this.addressDataForm.controls;
  }

  ngOnInit(): void {
    if (this.isEditMode) {
      this.iniciaFormulario(this.data.address);
    }

    const sub = this.addressService.getAddresses().subscribe({
      next: (data) => {
        const userAddresses = data?.enderecos || [];
        const hasInvoicingType = userAddresses.some((addr: Address) => addr.idTipo === 1);
        let isInvoicingType = false;
        if (this.isEditMode && hasInvoicingType) {
          isInvoicingType = this.data.address?.idTipo === 1;
        }
        this.listAddressType =
          isInvoicingType || (!hasInvoicingType && !this.isEditMode)
            ? [{ value: 'Faturamento e/ou Entrega', viewValue: 'Faturamento e/ou Entrega' }]
            : !this.isEditMode && hasInvoicingType
            ? [{ value: 'Entrega', viewValue: 'Entrega' }]
            : [
                { value: 'Faturamento e/ou Entrega', viewValue: 'Faturamento e/ou Entrega' },
                { value: 'Entrega', viewValue: 'Entrega' }
              ];

        if (this.listAddressType && this.listAddressType.length === 1) {
          const unicoValor = this.listAddressType[0].value;
          this.addressDataForm.get('addressType')?.setValue(unicoValor as string);
          this.addressDataForm.get('addressType')?.disable();
        }
        this.cdr.markForCheck();
      }
    });

    this.subscriptions.push(sub);
  }

  ngAfterViewInit(): void {
    fromEvent(this.dialogScrollAreaRef.nativeElement, 'scroll')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const scrollTop = this.dialogScrollAreaRef.nativeElement.scrollTop;
        const wasScrolled = this.hasScrolled;
        this.hasScrolled = scrollTop > 0;

        if (wasScrolled !== this.hasScrolled) {
          this.cdr.markForCheck(); // Atualiza a view
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSave(): void {
    this.addressDataFormControl.state.enable();
    this.addressDataFormControl.street.enable();
    this.addressDataFormControl.district.enable();
    this.addressDataFormControl.city.enable();
    const cep = this.addressDataFormControl.zipCode.value!;
    const formattedCep = `${cep.slice(0, 5)}-${cep.slice(5)}`;

    let logradouro: string | null = null;
    let bairro: string | null = null;

    if (this.cepResponse?.nomeLogradouro === null) {
      logradouro = this.addressDataFormControl.street.value;
    }

    if (this.cepResponse?.nomeBairro === null) {
      bairro = this.addressDataFormControl.district.value;
    }

    const newAddress = {
      tipo: this.addressDataFormControl.addressType.value === 'Entrega' ? '3' : '1',
      cep: formattedCep,
      numero: this.addressDataFormControl.number.value,
      complemento: this.addressDataFormControl.complement.value,
      responsavel: this.addressDataFormControl.responsible.value,
      referencia: this.addressDataFormControl.reference.value,
      logradouro: logradouro,
      bairro: bairro
    };

    this.dialogRef.close({ action: 'save', data: newAddress });
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  onDelete(): void {
    this.dialogRef.close({ action: 'delete' });
  }

  iniciaFormulario(address?: Address): void {
    if (!address) return;
    this.addressDataFormControl.zipCode.setValue(address.numeroCep?.replace('-', '') || '');
    this.findMethod(address);
  }

  public readonly findMethod = (address?: Address) => {
    if (this.addressDataFormControl.zipCode.invalid) {
      this.showFields = false;
      return;
    }

    const zip = this.addressDataFormControl.zipCode.value!;
    this.newZipCode = `${zip.substring(0, 5)}-${zip.substring(5, 8)}`;

    const sub = this.addressService.getCep(this.newZipCode).subscribe({
      next: (data) => {
        this.cepResponse = data;

        const logradouro = data.nomeLogradouro ?? '';
        const bairro = data.nomeBairro ?? '';
        const cidade = data.nomeCidade ?? '';
        const estado = data.codigoEstado ?? '';

        if (data.nomeLogradouro != null) {
          this.addressDataFormControl.street.disable();
        } else {
          this.addressDataFormControl.street.enable();
          this.addressDataFormControl.street.markAsUntouched();
        }
        if (data.nomeBairro != null) {
          this.addressDataFormControl.district.disable();
        } else {
          this.addressDataFormControl.district.enable();
          this.addressDataFormControl.district.markAsUntouched();
        }

        this.addressDataForm.patchValue({
          street: logradouro,
          district: bairro,
          city: cidade,
          state: estado
        });

        this.showFields = true;

        if (this.isEditMode && address) {
          this.addressDataForm.patchValue({
            addressType: address.descricaoTipo || this.addressDataFormControl.addressType.value,
            street: logradouro || address.logradouro,
            district: bairro || address.bairroCep,
            complement: address.complemento,
            reference: address.pontoReferencia,
            number: address.numero,
            responsible: address.responsavel
          });
        }

        this.cdr.markForCheck();
      },
      error: () => {
        this.showFields = false;
        this.cdr.markForCheck();
      }
    });

    this.subscriptions.push(sub);
  };
}
